import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import client, { apiErr } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, MapPin, KeyRound, Car, Radar, Pencil, Power, Trash2, Loader2, Briefcase, ClipboardList, Settings2, Navigation } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useCustomFields, CustomFieldsView, CustomFieldsEdit, CustomFieldManager } from "@/components/CustomFields";
import PropertyThread from "@/components/PropertyThread";
import { confirm } from "@/components/ConfirmDialog";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { SearchSelect } from "@/components/SearchSelect";
import { hoursToHM } from "@/lib/duration";
import { MapLink } from "@/components/MapLink";
import { PhotoTypeChecks, PHOTO_REQ } from "@/components/RequiredPhotos";
import { PropertyFrontPhoto } from "@/components/PropertyFrontPhoto";

const EMPTY = { clientId: "", address: "", geo: { lat: "", lng: "" }, geofenceRadius: 25, geofenceEnabled: false, accessInfo: "", parkingInfo: "", what3words: "", instructions: "", notes: "", assignedWorkerIds: [] };

export default function PropertyProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t } = useI18n();
  const { can, messagingEnabled } = useAuth();
  const [p, setP] = useState(null);
  const [clients, setClients] = useState([]);
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [cfValues, setCfValues] = useState({});
  const [cfMgr, setCfMgr] = useState(false);
  const { defs: cfDefs, reload: reloadDefs } = useCustomFields("property");

  const load = useCallback(() => {
    client.get(`/properties/${id}`).then((r) => {
      setP(r.data);
      setEdit({ ...EMPTY, ...r.data, geo: { lat: r.data.geo?.lat ?? "", lng: r.data.geo?.lng ?? "" } });
      setCfValues(r.data.customFields || {});
    }).catch((e) => toast.error(apiErr(e.response?.data?.detail)));
  }, [id]);
  useEffect(() => { load(); client.get("/clients").then((r) => setClients(r.data)).catch(() => {}); }, [load]);

  const clientName = clients.find((c) => c.id === p?.clientId)?.name || "—";

  const createClientInline = async (name) => {
    const { data } = await client.post("/clients", { name, contact: "" });
    const list = (await client.get("/clients")).data; setClients(list);
    const id = data?.id || (list.find((c) => c.name === name) || {}).id;
    setEdit((f) => ({ ...f, clientId: id }));
    toast.success(t("toast.clientSaved", "Client saved"));
  };

  const save = async () => {
    setBusy(true);
    try {
      await client.patch(`/properties/${id}`, {
        clientId: edit.clientId, address: edit.address,
        geo: { lat: parseFloat(edit.geo.lat) || null, lng: parseFloat(edit.geo.lng) || null },
        geofenceRadius: Number(edit.geofenceRadius) || 25,
        geofenceEnabled: !!edit.geofenceEnabled,
        accessInfo: edit.accessInfo, parkingInfo: edit.parkingInfo,
        what3words: edit.what3words, instructions: edit.instructions, notes: edit.notes,
        assignedWorkerIds: edit.assignedWorkerIds || [],
        requiredPhotoTypes: edit.requiredPhotoTypes || [],
      });
      if (cfDefs.length) await client.put(`/custom-values/property/${id}`, { values: cfValues });
      toast.success(t("common.saved", "Saved")); setEditing(false); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const lifecycle = async (action) => {
    const verb = action === "delete" ? t("prop.delConfirm", "Delete this property? Only possible if no jobs are linked.") : t(`prop.${action}Confirm`, `${action} this property?`);
    if (!(await confirm({ title: t(`prop.${action}`, action), description: verb, confirmText: t(`prop.${action}`, action), destructive: action !== "reactivate" }))) return;
    try {
      if (action === "delete") { await client.delete(`/properties/${id}`); toast.success(t("common.saved", "Done")); nav("/properties"); return; }
      await client.post(`/properties/${id}/${action}`); toast.success(t("common.saved", "Done")); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const directions = () => {
    const g = p.geo;
    const dest = g?.lat != null ? `${g.lat},${g.lng}` : encodeURIComponent(p.address || "");
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${dest}`, "_blank");
  };

  if (!p) return <div className="p-8 text-slate-400" data-testid="property-profile-loading">Loading…</div>;
  const inactive = p.status === "deactivated";
  const jobs = p.relatedJobs || [];
  const groups = p.jobGroups || [];

  return (
    <div className="space-y-6" data-testid="property-profile-page">
      <button onClick={() => nav("/properties")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-brand" data-testid="property-back">
        <ArrowLeft className="h-4 w-4" />{t("nav.properties", "Properties")}
      </button>

      <Card className="p-6 border-slate-200">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex flex-col sm:flex-row sm:items-start gap-4 w-full sm:w-auto">
            <PropertyFrontPhoto propertyId={id} fileId={p.frontPhotoId} onChange={(fid) => setP((x) => ({ ...x, frontPhotoId: fid }))} />
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-brand flex items-center gap-1"><Briefcase className="h-3 w-3" />{clientName}</p>
              <h2 className="font-display text-2xl font-bold text-brand-ink flex items-center gap-2"><MapLink address={p.address} geo={p.geo} testid="pp-address-link" className="text-brand-ink" />{inactive && <Badge variant="outline" className="bg-slate-100 text-slate-500">{t("staff.inactive", "Inactive")}</Badge>}</h2>
              <div className="flex gap-2 mt-2">
                <Badge variant="outline" className="bg-slate-50"><Radar className="h-3 w-3 mr-1 text-teal-500" />{p.geofenceRadius}m</Badge>
                <Badge variant="outline" className="bg-slate-50">{groups.length} {t("prop.jobs", "jobs")} · {jobs.length} visits</Badge>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="property-actions">
            <Button size="sm" variant="outline" data-testid="property-directions" onClick={directions}><Navigation className="h-3.5 w-3.5 mr-1.5" />{t("job.directions", "Get directions")}</Button>
            {can("property.manage") && !editing && <Button size="sm" variant="outline" data-testid="property-edit-btn" onClick={() => setEditing(true)}><Pencil className="h-3.5 w-3.5 mr-1.5" />{t("common.edit", "Edit")}</Button>}
            {can("property.manage") && <Button size="sm" variant="outline" data-testid="property-manage-fields" onClick={() => setCfMgr(true)}><Settings2 className="h-3.5 w-3.5 mr-1.5" />{t("cf.manage", "Custom fields")}</Button>}
            {can("property.manage") && (inactive
              ? <Button size="sm" variant="outline" data-testid="property-reactivate" onClick={() => lifecycle("reactivate")}><Power className="h-3.5 w-3.5 mr-1.5" />{t("prop.reactivate", "Reactivate")}</Button>
              : <Button size="sm" variant="outline" className="text-amber-600 border-amber-200" data-testid="property-deactivate" onClick={() => lifecycle("deactivate")}><Power className="h-3.5 w-3.5 mr-1.5" />{t("prop.deactivate", "Deactivate")}</Button>)}
            {can("property.manage") && <Button size="sm" variant="outline" className="text-red-600 border-red-200" data-testid="property-delete" onClick={() => lifecycle("delete")}><Trash2 className="h-3.5 w-3.5 mr-1.5" />{t("common.delete", "Delete")}</Button>}
          </div>
        </div>
      </Card>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview" data-testid="pp-tab-overview">{t("prop.tab.overview", "Overview")}</TabsTrigger>
          <TabsTrigger value="access" data-testid="pp-tab-access">{t("prop.tab.access", "Access")}</TabsTrigger>
          <TabsTrigger value="jobs" data-testid="pp-tab-jobs">Jobs ({groups.length})</TabsTrigger>
          {messagingEnabled && <TabsTrigger value="messages" data-testid="pp-tab-messages">{t("site.messages", "Messages")}</TabsTrigger>}
        </TabsList>

        <TabsContent value="overview" className="pt-4">
          <Card className="p-5 border-slate-200 max-w-2xl space-y-3">
            {editing ? (
              <>
                <div><Label>{t("prop.client", "Client")}</Label>
                  <SearchSelect
                    testid="property-edit-client"
                    value={edit.clientId}
                    onChange={(v) => setEdit({ ...edit, clientId: v })}
                    options={clients.map((c) => ({ value: c.id, label: c.name }))}
                    placeholder={t("prop.client", "Client")}
                    searchPlaceholder={t("job.searchClient", "Search or add a client…")}
                    onCreate={createClientInline}
                    createLabel={t("job.addClient", "Create client")}
                    className="mt-1.5"
                  />
                </div>
                <div><Label>{t("prop.address", "Address")}</Label><AddressAutocomplete testid="property-edit-address" value={edit.address} onChange={(v) => setEdit({ ...edit, address: v })} onSelect={(r) => setEdit((f) => ({ ...f, address: r.address, geo: { lat: r.lat ?? "", lng: r.lng ?? "" } }))} className="mt-1.5" /></div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Lat</Label><Input value={edit.geo.lat} onChange={(e) => setEdit({ ...edit, geo: { ...edit.geo, lat: e.target.value } })} className="mt-1.5" /></div>
                  <div><Label>Lng</Label><Input value={edit.geo.lng} onChange={(e) => setEdit({ ...edit, geo: { ...edit.geo, lng: e.target.value } })} className="mt-1.5" /></div>
                  <div><Label>{t("sites.geofence", "Geofence (m)")}</Label><Input type="number" value={edit.geofenceRadius} onChange={(e) => setEdit({ ...edit, geofenceRadius: e.target.value })} className="mt-1.5" /></div>
                </div>
                <label className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5">
                  <span className="text-sm text-slate-700">{t("geo.enforce", "Enforce geofence — block clock in/out outside the radius")}</span>
                  <Switch data-testid="pp-geofence-toggle" checked={!!edit.geofenceEnabled} onCheckedChange={(v) => setEdit({ ...edit, geofenceEnabled: v })} />
                </label>
                <div className="rounded-lg border border-slate-200 px-3 py-2.5 space-y-2" data-testid="pp-required-photos">
                  <p className="text-sm text-slate-700">Required photos for every job here (a job can override this)</p>
                  <PhotoTypeChecks value={edit.requiredPhotoTypes || []} onChange={(v) => setEdit({ ...edit, requiredPhotoTypes: v })} prefix="pp-required-photo" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>{t("prop.access", "Access")}</Label><Input value={edit.accessInfo} onChange={(e) => setEdit({ ...edit, accessInfo: e.target.value })} className="mt-1.5" /></div>
                  <div><Label>{t("prop.parking", "Parking")}</Label><Input value={edit.parkingInfo} onChange={(e) => setEdit({ ...edit, parkingInfo: e.target.value })} className="mt-1.5" /></div>
                  <div><Label>what3words</Label><Input value={edit.what3words} onChange={(e) => setEdit({ ...edit, what3words: e.target.value })} className="mt-1.5" /></div>
                </div>
                <div><Label>{t("prop.instructions", "Instructions")}</Label><Textarea value={edit.instructions} onChange={(e) => setEdit({ ...edit, instructions: e.target.value })} className="mt-1.5" /></div>
                <div><Label>{t("common.notes", "Notes")}</Label><Textarea value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} className="mt-1.5" /></div>
                <CustomFieldsEdit defs={cfDefs} values={cfValues} onChange={setCfValues} />
                <div className="flex gap-2 justify-end">
                  <Button variant="ghost" onClick={() => { setEditing(false); load(); }}>{t("common.cancel", "Cancel")}</Button>
                  <Button data-testid="property-edit-save" onClick={save} disabled={busy || !edit.clientId || !edit.address} className="bg-brand hover:bg-brand-dark">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.save", "Save")}</Button>
                </div>
              </>
            ) : (
              <>
                <Row icon={Briefcase} label={t("prop.client", "Client")} value={clientName} />
                <Row icon={MapPin} label={t("prop.address", "Address")} value={<MapLink address={p.address} geo={p.geo} />} />
                <Row icon={Radar} label={t("sites.geofence", "Geofence")} value={`${p.geofenceRadius}m · ${p.geofenceEnabled ? t("common.on", "On") : t("common.off", "Off")}`} />
                <Row icon={Radar} label="Required photos" value={(p.requiredPhotoTypes || []).length ? PHOTO_REQ.filter(([k]) => p.requiredPhotoTypes.includes(k)).map(([, l]) => l).join(", ") : "None"} />
                <Row icon={MapPin} label="GPS" value={p.geo?.lat != null ? `${p.geo.lat}, ${p.geo.lng}` : "—"} />
                {p.instructions && <p className="text-sm text-slate-600 border-t border-slate-100 pt-2 whitespace-pre-wrap">{p.instructions}</p>}
                {p.notes && <p className="text-xs text-slate-500">{p.notes}</p>}
                <Row icon={Power} label={t("common.status", "Status")} value={inactive ? t("staff.inactive", "Inactive") : t("st.active", "Active")} />
                <CustomFieldsView defs={cfDefs} values={p.customFields} />
                {p.client && (
                  <div className="border-t border-slate-100 pt-2" data-testid="pp-client-details">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Client details</p>
                    <Row icon={Briefcase} label="Name" value={<button type="button" className="text-brand font-semibold hover:underline" onClick={() => nav(`/clients/${p.client.id}`)} data-testid="pp-client-link">{p.client.name}</button>} />
                    {p.client.contact && <Row icon={Briefcase} label="Contact" value={p.client.contact} />}
                    {p.client.email && <Row icon={Briefcase} label="Email" value={<a href={`mailto:${p.client.email}`} className="text-brand hover:underline">{p.client.email}</a>} />}
                    {p.client.notes && <Row icon={Briefcase} label="Notes" value={p.client.notes} />}
                  </div>
                )}
              </>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="access" className="pt-4">
          <Card className="p-5 border-slate-200 max-w-2xl space-y-2">
            <Row icon={KeyRound} label={t("prop.access", "Access")} value={p.accessInfo || "—"} />
            <Row icon={Car} label={t("prop.parking", "Parking")} value={p.parkingInfo || "—"} />
            <Row icon={MapPin} label="what3words" value={p.what3words || "—"} />
          </Card>
        </TabsContent>

        <TabsContent value="jobs" className="pt-4">
          <Card className="border-slate-200 overflow-hidden">
            {groups.length === 0 ? <p className="p-6 text-sm text-slate-400">{t("prop.noJobs", "No jobs for this property.")}</p> : (
              <div className="divide-y divide-slate-50">
                {groups.map((g) => (
                  <button key={g.openId} data-testid={`pp-job-group-${g.jobNumber ?? g.openId}`} onClick={() => nav(`/jobs/${g.openId}`)}
                    className="w-full flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-left hover:bg-slate-50 transition">
                    <span className="font-mono font-semibold text-brand w-14">{g.jobNumber != null ? `#${g.jobNumber}` : "—"}</span>
                    <span className="text-sm text-slate-800 flex-1 min-w-[140px]">{t(`jt.${g.jobType}`, g.jobType)} · {g.visits} visit{g.visits === 1 ? "" : "s"}{g.upcoming ? ` (${g.upcoming} upcoming)` : ""}</span>
                    <span className="text-xs text-slate-500">{g.nextDate ? `Next ${g.nextDate}` : `Last ${g.lastDate}`}</span>
                    <Badge variant="outline" className="bg-slate-50">{hoursToHM(g.hours)} worked</Badge>
                  </button>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="messages" className="pt-4">
          {messagingEnabled && <PropertyThread propertyId={id} title={p.address} />}
        </TabsContent>
      </Tabs>

      <CustomFieldManager entity="property" open={cfMgr} onOpenChange={setCfMgr} onChanged={reloadDefs} />
    </div>
  );
}

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3 py-1.5 border-b border-slate-50 last:border-0">
    <Icon className="h-4 w-4 text-slate-400 shrink-0" /><span className="text-xs uppercase tracking-wide text-slate-400 w-28 shrink-0">{label}</span><span className="text-sm text-slate-700 break-words">{value}</span>
  </div>
);
